import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Plus, Trash2, Briefcase, Loader2, Search, Download, Upload } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminJobFunctions = () => {
  const { getAuthHeaders } = useAuth();
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [search, setSearch] = useState("");
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => { loadFunctions(); }, []);

  const loadFunctions = async () => {
    try {
      const res = await axios.get(`${API}/job-functions/all`, { headers: getAuthHeaders() });
      setFunctions(res.data);
    } catch (err) {
      toast.error("Erro ao carregar funções");
    } finally {
      setLoading(false);
    }
  };

  const addFunction = async () => {
    if (!newName.trim()) return;
    try {
      const res = await axios.post(`${API}/job-functions`, { name: newName.trim(), category: "IT" }, { headers: getAuthHeaders() });
      setFunctions(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewName("");
      toast.success("Função adicionada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao adicionar");
    }
  };

  const deleteFunction = async (id) => {
    try {
      await axios.delete(`${API}/job-functions/${id}`, { headers: getAuthHeaders() });
      setFunctions(prev => prev.filter(f => f.id !== id));
      toast.success("Função eliminada");
    } catch (err) {
      toast.error("Erro ao eliminar");
    }
  };

  const filtered = functions.filter(f => f.name.toLowerCase().includes(search.toLowerCase()));

  // ===== Excel Export =====
  const exportToExcel = () => {
    if (functions.length === 0) { toast.error("Sem funções para exportar"); return; }
    const rows = functions.map(f => ({
      Nome: f.name,
      Categoria: f.category || "IT",
      Descrição: f.description || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 30 }, { wch: 14 }, { wch: 40 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Funções");
    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `evolutt-funcoes-${date}.xlsx`);
    toast.success(`${functions.length} função(ões) exportada(s)`);
  };

  // ===== Excel Import =====
  const handleImportClick = () => fileInputRef.current?.click();

  const handleFileSelected = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);
      if (!rows.length) { toast.error("Ficheiro vazio"); return; }
      const existing = new Set(functions.map(f => f.name.toLowerCase()));
      let added = 0, skipped = 0;
      for (const row of rows) {
        const name = String(row.Nome || row.nome || row.name || "").trim();
        if (!name) { skipped++; continue; }
        if (existing.has(name.toLowerCase())) { skipped++; continue; }
        const category = String(row.Categoria || row.categoria || row.category || "IT").trim() || "IT";
        const description = String(row.Descrição || row.descricao || row.description || "").trim();
        try {
          await axios.post(`${API}/job-functions`, { name, category, description }, { headers: getAuthHeaders() });
          added++;
        } catch (e) {
          skipped++;
        }
      }
      toast.success(`Importação concluída: +${added} novo(s), ${skipped} ignorado(s)`);
      await loadFunctions();
    } catch (e) {
      toast.error("Erro ao ler ficheiro: " + (e.message || e));
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--brand-primary)]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-job-functions">
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-2" />Voltar</Button>
            </Link>
            <Briefcase className="w-5 h-5 text-[var(--brand-primary)]" />
            <span className="font-manrope font-bold text-lg">Funções / Cargos</span>
            <span className="text-xs text-muted-foreground bg-slate-100 px-2 py-0.5 rounded">{functions.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelected}
              className="hidden"
              data-testid="import-file-input"
            />
            <Button
              variant="outline" size="sm"
              onClick={handleImportClick}
              disabled={importing}
              data-testid="import-functions-btn"
            >
              {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Importar
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={exportToExcel}
              data-testid="export-functions-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Add new */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Adicionar Função</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Ex: Cloud Architect"
                className="flex-1"
                onKeyDown={e => e.key === "Enter" && addFunction()}
                data-testid="new-function-input"
              />
              <Button onClick={addFunction} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary)] text-white" data-testid="add-function-btn">
                <Plus className="w-4 h-4 mr-2" />
                Adicionar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Search + List */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Pesquisar funções..."
            className="pl-10"
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {filtered.map(f => (
            <div
              key={f.id}
              className="flex items-center justify-between p-3 bg-white border rounded-lg hover:border-[var(--brand-primary)]/30 transition-colors group"
            >
              <span className="text-sm font-medium text-slate-700 truncate">{f.name}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => deleteFunction(f.id)}
                className="opacity-0 group-hover:opacity-100 h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="text-center text-sm text-muted-foreground py-8">Nenhuma função encontrada.</p>
        )}
      </main>
    </div>
  );
};

export default AdminJobFunctions;
